import React, { useEffect, useMemo, useRef } from "react";

import { ApiError } from "./common/apiErrors";
import { canEditProject } from "./common/projectRole";
import CardGenerateDirectScript from "./CardGenerateDirectScript";
import {
  AiUsageThreadDetail,
  CARD_NAME,
  CheckCostResponse,
  DirectScriptInput,
  FinalScriptData,
  GenerateCharacterPayload,
  ImageAsset,
  RefineScriptToScenePayload,
  RefineScriptToSceneResponse,
  SegmentVersionResponse,
  StartGenerateDirectScriptPayload,
  UpdateTitlePayload,
  UploadCharacterPayload,
  AssetType,
} from "./types/MeGenTypes";

/**
 * จอ run ของ script2vdo พร้อมสายไฟครบชุด — `CardGenerateDirectScript` รับ props 49 ตัว
 * (เป็น callback 27 ตัวที่เป็น wrapper บาง ๆ ของ onEvent) ที่นี่คือที่เดียวที่ประกอบให้
 * ใช้ได้ทั้งในแท็บ MeGen เองและจากโมดูลอื่น (เช่น MePOS เปิดดู run ของผลแลป) โดยไม่ต้องก็อปสายไฟไป
 *
 * เปิดจากนอก MeGen ให้ส่ง projectId/threadId มาเอง — ไม่ส่งจะอ่านจาก MeGenSequence เหมือนตอนอยู่ในแท็บ
 */
type MeGenRunViewProps = {
  MeGenSequence: any;
  onEvent: (e: any) => void;
  setProp: (key: any, value: any) => any;
  runSequence?: any;
  BillingAndPackageSequence?: any;
  selectedDivisionId?: any;
  projectId?: number;
  threadId?: string | null;
  canEdit?: boolean;
  canViewAiUsage?: boolean;
  defaultFinalOutput?: boolean;
  defaultAudioOnly?: boolean;
  onNavigateBack?: () => void;
};

const MeGenRunView = (props: MeGenRunViewProps) => {
  const sequence = props.MeGenSequence;
  const selectedProject = sequence?.selectedProject;
  const selectedSeries = sequence?.selectedSeries;

  const projectId = props.projectId ?? selectedProject?.id;
  const threadId = props.threadId ?? sequence?.currentThreadId ?? null;

  const currentProject = (sequence?.projectList || []).find((item: any) => item.id === projectId);

  // เปิดจากโมดูลอื่นจะยังไม่เคยผ่าน bootstrap ของ MeGen (HandleStartMeGen) ⇒ ตัวเลือกพวกนี้จะว่าง
  // โหลดเองเมื่อยังไม่มี ส่วนในแท็บ MeGen ที่ bootstrap ไปแล้วจะไม่ยิงซ้ำ
  useEffect(() => {
    const fetches: [boolean, string][] = [
      [!sequence?.voiceList?.length, "HandleGetVoiceList"],
      [!sequence?.mediaStyleList?.length, "HandleGetMediaStyles"],
      [!sequence?.imageModelCatalog, "HandleGetImageModels"],
      [!sequence?.videoModelCatalog, "HandleGetVideoModels"],
    ];

    fetches.forEach(([missing, message]) => {
      if (missing) {
        props.onEvent({ message, params: {} });
      }
    });
  }, [
    sequence?.voiceList,
    sequence?.mediaStyleList,
    sequence?.imageModelCatalog,
    sequence?.videoModelCatalog,
  ]);

  // การ์ดโหลด state ของ run ผ่าน polling ซึ่งเริ่มเมื่อ isGenerating เป็น true เท่านั้น
  // ทางปกติของ MeGen มี HandleSelectEpisode เป็นคนเคาะให้ เปิดตรงจากที่อื่นจึงต้องเคาะรอบแรกเอง
  // (polling จะปิด flag ให้เองเมื่อ state กลับมาว่าไม่ได้ generate อยู่)
  const kickedThreadRef = useRef<string | null>(null);

  useEffect(() => {
    if (!threadId || kickedThreadRef.current === threadId) {
      return;
    }

    kickedThreadRef.current = threadId;

    props.setProp("MeGenSequence.isGenerating", true);
  }, [threadId]);

  const ownerNameMap = useMemo(() => {
    const map: Record<string, string> = {};

    (sequence?.allOaUsersList || []).forEach((user: any) => {
      const userId = String(user.user);

      if (userId) {
        map[userId] = user.user_profile?.fullname || user.user_profile?.username || `User ${userId}`;
      }
    });

    return map;
  }, [sequence?.allOaUsersList]);

  const handleCheckGenerateCost = (
    threadId: string,
    data: StartGenerateDirectScriptPayload,
    onSuccess?: (result: CheckCostResponse) => void,
    onError?: (err: ApiError) => void
  ) => {
    props.onEvent({
      message: "HandleCheckGenerateCost",
      params: { threadId, data, onSuccess, onError },
    });
  };

  const handleCreateScriptToSceneRun = (
    projectId: number,
    seriesId: number | null,
    onSuccess: (threadId: string) => void
  ) => {
    props.onEvent({
      message: "HandleCreateScriptToSceneRun",
      params: { projectId, seriesId, onSuccess },
    });
  };

  const handleDeleteCharacter = (characterId: number, seriesId: number) => {
    props.onEvent({
      message: "HandleDeleteCharacter",
      params: { characterId, seriesId },
    });
  };

  const handleDeleteImageAsset = (
    threadId: string,
    filename: string,
    onSuccess?: (assets: ImageAsset[]) => void,
    onError?: () => void
  ) => {
    props.onEvent({
      message: "HandleDeleteImageAsset",
      params: { threadId, filename, onSuccess, onError },
    });
  };

  const handleDirectScriptGenerate = (
    threadId: string | null,
    projectId: number,
    seriesId: number | null,
    data: DirectScriptInput,
    dryRun: boolean
  ) => {
    props.onEvent({
      message: "HandleDirectScriptGenerate",
      params: { threadId, projectId, seriesId, data, dryRun },
    });
  };

  const handleFetchAiUsage = (
    threadId: string,
    onSuccess: (detail: AiUsageThreadDetail) => void,
    onError: (message: string) => void,
  ) => {
    props.onEvent({
      message: "HandleGetAiUsageThreadDetail",
      params: { threadId, onSuccess, onError },
    });
  };

  const handleGenerateCharacter = (data: GenerateCharacterPayload) => {
    props.onEvent({
      message: "HandleGenerateCharacter",
      params: { data },
    });
  };

  const handleGenerateDirectScriptVideo = (
    threadId: string,
    data: StartGenerateDirectScriptPayload
  ) => {
    props.onEvent({
      message: "HandleStartGenerateDirectScript",
      params: {
        threadId,
        data,
      },
    });
  };

  const handleGenerateDirectScriptVideoFinal = (
    threadId: string,
    data: StartGenerateDirectScriptPayload
  ) => {
    props.onEvent({
      message: "HandleStartGenerateDirectScriptFinal",
      params: {
        threadId,
        data,
      },
    });
  };

  const handleGenerateScene = (
    threadId: string,
    data: StartGenerateDirectScriptPayload,
    onSuccess?: () => void
  ) => {
    props.onEvent?.({
      message: "HandleGenerateScene",
      params: { threadId, data, onSuccess },
    });
  };

  const handleGetCharacterList = (seriesId: number) => {
    props.onEvent({
      message: "HandleGetCharacterList",
      params: { seriesId },
    });
  };

  const handleGetImageAssets = (
    threadId: string,
    onSuccess?: (assets: ImageAsset[]) => void,
    onError?: () => void
  ) => {
    props.onEvent({
      message: "HandleGetImageAssets",
      params: { threadId, onSuccess, onError },
    });
  };

  const handleGetScriptToSceneState = (
    threadId: string,
    onSuccess?: (newState: any, isGenerating: boolean) => void
  ) => {
    props.onEvent({
      message: "HandleGetScriptToSceneState",
      params: { threadId, onSuccess },
    });
  };

  const handleGetSegmentVersions = (
    threadId: string,
    segmentId: string,
    assetType: AssetType,
    onSuccess: (data: SegmentVersionResponse) => void,
    // opts: silent = auto-fetch เบื้องหลัง (พังแล้วเงียบ) · onError = ผู้ใช้กดเอง จะได้ปิด panel ที่รออยู่
    opts?: { onError?: (err: ApiError) => void; silent?: boolean }
  ) => {
    props.onEvent({
      message: "HandleGetSegmentVersions",
      params: { threadId, segmentId, assetType, onSuccess, ...opts },
    });
  };

  const handleNavigateBackToProject = () => {
    props.onEvent({
      message: "HandleNaviagateToProjectPage",
      params: {},
    });
  };

  const handleRefineScriptToScene = (
    threadId: string,
    data: RefineScriptToScenePayload,
    onSuccess?: (result: RefineScriptToSceneResponse) => void,
    onError?: () => void
  ) => {
    props.onEvent({
      message: "HandleRefineScriptToScene",
      params: { threadId, data, onSuccess, onError },
    });
  };

  const handleResumeUploadDirectScript = (
    threadId: string,
    credentialId: number | null,
    onSuccess?: (
      newState: any,
      isGenerating: boolean
    ) => void
  ) => {
    props.onEvent({
      message: "HandleUploadYouTubeDirectScript",
      params: {
        threadId,
        credentialId,
        onSuccess,
      },
    });
  };

  const handleSetCurrentVersion = (
    threadId: string,
    segmentId: string,
    assetType: AssetType,
    version: number,
    onSuccess?: () => void
  ) => {
    props.onEvent({
      message: "HandleSetCurrentVersion",
      params: { threadId, segmentId, assetType, version, onSuccess },
    });
  };

  const handleStartScriptToScene = (
    threadId: string,
    script: string,
    onSuccess?: (threadId: string) => void,
  ) => {
    props.onEvent({
      message: "HandleStartScriptToScene",
      params: { threadId, script, onSuccess },
    });
  };

  const handleStopDirectScript = (
    threadId: string,
    onSuccess?: () => void,
  ) => {
    props.onEvent({
      message: "HandleStopRun",
      params: { threadId, onSuccess },
    });
  };

  const handleUpdateDirectScript = (
    threadId: string,
    data: FinalScriptData
  ) => {
    props.onEvent({
      message: "HandleUpdateDirectScript",
      params: { threadId, data },
    });
  };

  const handleUpdateDirectScriptTitle = (
    threadId: string,
    data: UpdateTitlePayload,
    onSuccess?: (title: string) => void,
    onError?: () => void,
  ) => {
    props.onEvent({
      message: "HandleUpdateDirectScriptTitle",
      params: {
        threadId,
        data,
        onSuccess,
        onError
      }
    });
  };

  const handleUploadCharacter = (data: UploadCharacterPayload) => {
    props.onEvent({
      message: "HandleUploadCharacter",
      params: { data },
    });
  };

  const handleUploadDirectImage = (
    threadId: string,
    itemId: string,
    file: File,
    onSuccess?: (url: string) => void,
    onError?: () => void
  ) => {
    props.onEvent({
      message: "HandleUploadDirectImage",
      params: {
        threadId,
        itemId,
        file,
        onSuccess,
        onError,
      },
    });
  };

  const handleUploadDirectVideo = (
    threadId: string,
    itemId: string,
    file: File,
    onSuccess?: (url: string) => void,
    onError?: () => void
  ) => {
    props.onEvent({
      message: "HandleUploadDirectVideo",
      params: {
        threadId,
        itemId,
        file,
        onSuccess,
        onError,
      },
    });
  };

  const handleUploadImageAsset = (
    threadId: string,
    file: File,
    onSuccess?: (assets: ImageAsset[]) => void,
    onError?: () => void
  ) => {
    props.onEvent({
      message: "HandleUploadImageAsset",
      params: { threadId, file, onSuccess, onError },
    });
  };

  const handleUploadImageOverlay = (
    threadId: string,
    itemId: string,
    file: File,
    onSuccess?: (url: string) => void,
    onError?: () => void
  ) => {
    props.onEvent({
      message: "HandleUploadImageOverlay",
      params: {
        threadId,
        itemId,
        file,
        onSuccess,
        onError,
      },
    });
  };

  return (
    <CardGenerateDirectScript
      projectId={projectId}
      canEdit={props.canEdit ?? canEditProject(selectedProject?.role)}
      canViewAiUsage={props.canViewAiUsage}
      defaultFinalOutput={props.defaultFinalOutput}
      defaultAudioOnly={props.defaultAudioOnly}
      onFetchAiUsage={handleFetchAiUsage}
      aiUsageUserNameMap={ownerNameMap}
      projectName={currentProject?.name || ""}
      seriesId={selectedSeries?.id}
      seriesName={selectedSeries?.name}
      seriesList={(sequence?.seriesList || []).map((item: any) => ({ id: item.id, name: item.name }))}
      threadId={threadId}
      runTitle={
        // อ่านจาก runIndex (แคชสะสมทุก run ที่เคยโหลด) ไม่ใช่ผลค้นหาของแท็บ (runsVideos/runsPlaylist)
        // ไม่งั้นเปิด run ที่ไม่อยู่ในผลค้นหาปัจจุบันแล้วชื่อจะหายเงียบ ๆ
        threadId ? sequence?.runIndex?.[threadId]?.title : undefined
      }
      mediaStyleList={sequence?.mediaStyleList}
      voiceList={sequence?.voiceList || []}
      imageModelCatalog={sequence?.imageModelCatalog ?? null}
      videoModelCatalog={sequence?.videoModelCatalog ?? null}
      isRunning={sequence?.isGenerating || false}
      isScriptToSceneRunning={sequence?.isScriptToSceneGenerating || false}
      finalScript={(sequence?.graphState?.values as any)?.final_script}
      youtubeChannels={sequence?.youtubeStatus?.channels || []}
      characterList={sequence?.characterList || []}
      isCharacterGenerating={sequence?.isCharacterGenerating || false}
      onNavigateBack={props.onNavigateBack ?? handleNavigateBackToProject}
      onDownloadError={(message: string) => props.setProp(`errorMessage.${CARD_NAME}`, message)}
      onDirectGenerate={handleDirectScriptGenerate}
      onGenerateVideo={handleGenerateDirectScriptVideo}
      onGenerateVideoFinal={handleGenerateDirectScriptVideoFinal}
      onUpdateScript={handleUpdateDirectScript}
      onUploadYoutube={handleResumeUploadDirectScript}
      onUploadVideo={handleUploadDirectVideo}
      onUploadImage={handleUploadDirectImage}
      onUploadImageOverlay={handleUploadImageOverlay}
      onUploadImageAsset={handleUploadImageAsset}
      onDeleteImageAsset={handleDeleteImageAsset}
      onGetImageAssets={handleGetImageAssets}
      onGetSegmentVersions={handleGetSegmentVersions}
      onSetCurrentVersion={handleSetCurrentVersion}
      onGeneratingChange={(isGenerating) => {
        props.setProp("MeGenSequence.isGenerating", isGenerating);
      }}
      onCheckCost={handleCheckGenerateCost}
      onGenerateScene={handleGenerateScene}
      onStopRun={handleStopDirectScript}
      onUpdateTitle={handleUpdateDirectScriptTitle}
      onGetCharacterList={handleGetCharacterList}
      onGenerateCharacter={handleGenerateCharacter}
      onUploadCharacter={handleUploadCharacter}
      onDeleteCharacter={handleDeleteCharacter}
      // Script2Scene
      onCreateScriptToSceneRun={handleCreateScriptToSceneRun}
      onStartScriptToScene={handleStartScriptToScene}
      onGetScriptToSceneState={handleGetScriptToSceneState}
      onRefineScriptToScene={handleRefineScriptToScene}
      //
      onEvent={props.onEvent}
      // BillingAndPackage
      runSequence={props.runSequence}
      BillingAndPackageSequence={props.BillingAndPackageSequence}
      selectedDivisionId={props.selectedDivisionId}
    />
  );
};

MeGenRunView.displayName = "MeGenRunView";

export default React.memo(MeGenRunView);
